import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { CrosswordPuzzle } from '@minicoursegenerator/crossword-puzzle'

// Default crossword config
const DEFAULT_CROSSWORD_CONFIG = {
    title: 'Sample Crossword',
    grid: [],
    clues: {
        across: [],
        down: []
    }
}

export default class CrosswordPuzzlePlugin extends Plugin {
    static get pluginName() {
        return 'CrosswordPuzzlePlugin'
    }

    static get requires() {
        return [Widget]
    }

    constructor(editor) {
        super(editor)
        this.mountedRoots = new Map()
        this.domElementMap = new Map()
        this._updateTimeout = null
        this._lastConfigStr = null
    }

    init() {
        const editor = this.editor

        editor.model.schema.register('crosswordPuzzle', {
            isObject: true,
            allowWhere: '$block',
            allowIn: 'column',
            allowAttributes: [
                'id',
                'data',
                'puzzleConfig',
            ],
        })

        this._defineConverters()
        this._setupAttributeListener()

        // Prevent keyboard deletion
        editor.editing.view.document.on(
            'keydown',
            (evt, data) => {
                const selection = editor.model.document.selection
                const selectedElement = selection.getSelectedElement()

                if (selectedElement && selectedElement.name === 'crosswordPuzzle') {
                    const domEvent = data.domEvent
                    const key = domEvent.key

                    const allowedKeys = [
                        'Escape',
                        'ArrowUp',
                        'ArrowDown',
                        'ArrowLeft',
                        'ArrowRight',
                        'Tab',
                    ]
                    if (allowedKeys.includes(key)) {
                        return
                    }

                    if (
                        key === 'Delete' ||
                        key === 'Backspace' ||
                        (key.length === 1 && !domEvent.ctrlKey && !domEvent.metaKey)
                    ) {
                        evt.stop()
                        data.preventDefault()
                    }
                }
            },
            { priority: 'highest' },
        )

        editor.commands.add('insertCrosswordPuzzle', {
            execute: () => {
                editor.model.change(writer => {
                    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
                        backgroundColor: '#EFE8D8',
                    })
                    const simpleBox = writer.createElement('simpleBox')
                    const column = writer.createElement('column')
                    const crosswordPuzzle = writer.createElement('crosswordPuzzle', {
                        puzzleConfig: JSON.stringify(DEFAULT_CROSSWORD_CONFIG),
                    })

                    writer.append(simpleBox, simpleBoxWrapper)
                    writer.append(column, simpleBox)
                    writer.append(crosswordPuzzle, column)

                    editor.model.insertContent(simpleBoxWrapper)
                    writer.setSelection(crosswordPuzzle, 'on')
                })
            },
        })
    }

    _setupAttributeListener() {
        const editor = this.editor

        editor.model.document.on('change:data', () => {
            const changes = editor.model.document.differ.getChanges()

            for (const change of changes) {
                if (change.type === 'attribute' && change.attributeKey === 'puzzleConfig') {
                    const modelElement = change.range.start.nodeAfter
                    if (modelElement && modelElement.name === 'crosswordPuzzle') {
                        const newPuzzleConfig = modelElement.getAttribute('puzzleConfig')
                        this._updateCrosswordComponent(modelElement, newPuzzleConfig)
                    }
                }
            }
        })
    }

    _updateCrosswordComponent(modelElement, puzzleConfig) {
        console.log('🧩 _updateCrosswordComponent called with:', puzzleConfig)

        // Skip if config hasn't changed
        if (this._lastConfigStr === puzzleConfig) {
            console.log('🧩 Config unchanged, skipping update')
            return
        }
        this._lastConfigStr = puzzleConfig

        const editor = this.editor
        const viewElement = editor.editing.mapper.toViewElement(modelElement)
        if (!viewElement) {
            console.log('🧩 No viewElement found')
            return
        }

        const domConverter = editor.editing.view.domConverter
        const containerDom = domConverter.mapViewToDom(viewElement)
        if (!containerDom) {
            console.log('🧩 No containerDom found')
            return
        }

        // Update the data-puzzle-config attribute on the container
        containerDom.setAttribute('data-puzzle-config', puzzleConfig)

        const placeholder = containerDom.querySelector('.crossword-puzzle-react-placeholder')
        if (!placeholder) {
            console.log('🧩 No placeholder found')
            return
        }

        // Force re-render by unmounting and remounting
        const existingRoot = this.mountedRoots.get(placeholder)
        if (existingRoot) {
            try {
                const parsedConfig = puzzleConfig ? JSON.parse(puzzleConfig) : DEFAULT_CROSSWORD_CONFIG
                console.log('🧩 CrosswordPuzzle re-rendering with new config:', parsedConfig)

                // Unmount and create new root for a fresh render
                existingRoot.unmount()
                this.mountedRoots.delete(placeholder)

                const newRoot = createRoot(placeholder)
                const props = {
                    key: `crossword-${Date.now()}`,
                    data: parsedConfig,
                    onComplete: () => console.log('🧩 Crossword completed!'),
                    onChange: state => console.log('🧩 State:', state),
                }
                newRoot.render(React.createElement(CrosswordPuzzle, props))
                this.mountedRoots.set(placeholder, newRoot)

                console.log('🧩 CrosswordPuzzle re-mounted successfully')
            } catch (error) {
                console.error('Error re-rendering CrosswordPuzzle component:', error)
            }
        } else {
            console.log('🧩 No existingRoot found, mounting new root')
            try {
                const parsedConfig = puzzleConfig ? JSON.parse(puzzleConfig) : DEFAULT_CROSSWORD_CONFIG
                const newRoot = createRoot(placeholder)
                const props = {
                    key: `crossword-${Date.now()}`,
                    data: parsedConfig,
                    onComplete: () => console.log('🧩 Crossword completed!'),
                    onChange: state => console.log('🧩 State:', state),
                }
                newRoot.render(React.createElement(CrosswordPuzzle, props))
                this.mountedRoots.set(placeholder, newRoot)
            } catch (error) {
                console.error('Error mounting CrosswordPuzzle component:', error)
            }
        }
    }

    _defineConverters() {
        const editor = this.editor
        const conversion = editor.conversion

        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                attributes: {
                    'data-type': 'crossword-puzzle',
                },
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('id')
                const data = viewElement.getAttribute('data') || ''
                const puzzleConfig = viewElement.getAttribute('data-puzzle-config') || ''

                const attrs = {
                    data,
                    puzzleConfig,
                }
                if (id) attrs.id = id

                return writer.createElement('crosswordPuzzle', attrs)
            },
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'crosswordPuzzle',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id')
                const data = modelElement.getAttribute('data') || ''
                const puzzleConfig = modelElement.getAttribute('puzzleConfig') || ''

                const attrs = {
                    'data-type': 'crossword-puzzle',
                    data: data,
                    'data-puzzle-config': puzzleConfig,
                }

                if (id !== undefined && id !== null) {
                    attrs.id = id
                }

                return writer.createEmptyElement('div', attrs)
            },
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'crosswordPuzzle',
            view: (modelElement, { writer }) => {
                const puzzleConfig = modelElement.getAttribute('puzzleConfig') || ''

                const containerAttrs = {
                    class: 'crossword-puzzle-widget-container',
                    'data-type': 'crossword-puzzle',
                    'data-puzzle-config': puzzleConfig,
                }

                const container = writer.createContainerElement('div', containerAttrs)

                const placeholder = writer.createRawElement(
                    'div',
                    {
                        class: 'crossword-puzzle-react-placeholder',
                    },
                    domElement => {
                        // Prevent events from reaching CKEditor
                        domElement.addEventListener('mousedown', e => e.stopPropagation())
                        domElement.addEventListener('click', e => e.stopPropagation())
                        domElement.addEventListener('dblclick', e => e.stopPropagation())
                        domElement.addEventListener('pointerdown', e => e.stopPropagation())
                        domElement.addEventListener('pointermove', e => e.stopPropagation())
                        domElement.addEventListener('pointerup', e => e.stopPropagation())
                        domElement.addEventListener('keydown', e => e.stopPropagation())
                        domElement.addEventListener('keyup', e => e.stopPropagation())
                        domElement.addEventListener('input', e => e.stopPropagation())
                        domElement.addEventListener('focus', e => e.stopPropagation(), true)
                        domElement.addEventListener('blur', e => e.stopPropagation(), true)

                        // Mount React component
                        console.log('🧩 Crossword Puzzle rendering started')
                        try {
                            const parsedConfig = puzzleConfig ? JSON.parse(puzzleConfig) : DEFAULT_CROSSWORD_CONFIG
                            const props = {
                                key: Date.now(),
                                data: parsedConfig,
                                onComplete: () => console.log('🧩 Crossword completed!'),
                                onChange: state => console.log('🧩 State:', state),
                            }

                            console.log('🧩 CrosswordPuzzle Props:', props)

                            const root = createRoot(domElement)
                            root.render(React.createElement(CrosswordPuzzle, props))

                            editor.plugins
                                .get('CrosswordPuzzlePlugin')
                                .mountedRoots.set(domElement, root)
                        } catch (error) {
                            console.error('Error rendering CrosswordPuzzle component:', error)
                            domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🧩</span>
                  <p>Crossword Puzzle</p>
                  <small>Component not available</small>
                </div>
              `
                        }
                    },
                )

                writer.insert(writer.createPositionAt(container, 0), placeholder)

                return toWidget(container, writer, {
                    label: 'Crossword Puzzle',
                    hasSelectionHandle: true,
                })
            },
        })
    }

    destroy() {
        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
            this._updateTimeout = null
        }

        for (const [domElement, root] of this.mountedRoots) {
            try {
                root.unmount()
            } catch (error) {
                console.error('Error unmounting React component:', error)
            }
        }
        this.mountedRoots.clear()
        super.destroy()
    }
}

export { CrosswordPuzzlePlugin }
